export const formatter = new Intl.DateTimeFormat('en-US', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * Date formatting function that handles both Date objects and strings.
 *
 * USAGE:
 *
 * - formatDate(new Date()) // Date object
 * - formatDate('2024-01-15') // ISO date string
 * - formatDate('01/15/2024') // MM/DD/YYYY string
 * - formatDate(null) // Returns null
 */
export function formatDate(d: Date | string | null | undefined): string | null {
  if (!d) return null;

  try {
    // If it's already a Date object, format it directly
    if (d instanceof Date) {
      return formatter.format(d);
    }

    // If it's a string, handle different formats
    const dateStr = String(d);

    // Check if it's already in MM/DD/YYYY format
    const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      return dateStr; // Already in correct format
    }

    // Check if it's in YYYY-MM-DD format (ISO date string)
    const hyphenMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (hyphenMatch) {
      const [, year, month, day] = hyphenMatch;
      return `${month}/${day}/${year}`;
    }

    // Try to parse as a Date object and format it
    const parsedDate = new Date(dateStr);
    if (!isNaN(parsedDate.getTime())) {
      return formatter.format(parsedDate);
    }

    // If all else fails, return the original string
    return dateStr;
  } catch (error) {
    return String(d);
  }
}

export function toDateString(d: Date | string): string {
  try {
    let newDate = new Date(d);
    const offset = newDate.getTimezoneOffset();
    newDate = new Date(newDate.getTime() - offset * 60 * 1000);
    return newDate.toISOString().split('T')[0];
  } catch (e) {
    return '';
  }
}

export function toDateTimeString(d: Date | string): string {
  try {
    const date = new Date(d);
    return new Intl.DateTimeFormat('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZone: 'America/New_York',
      timeZoneName: 'short',
    })
      .format(date)
      .replace(', ', ' ');
  } catch (e) {
    return '';
  }
}

export function formatDateWithSlashes(
  date: Date | string | null | undefined
): string {
  if (!date) return '';
  try {
    return formatter.format(new Date(date));
  } catch (e) {
    return '';
  }
}

/**
 * Format date with proper timezone handling to prevent date shifting.
 */
export function formatDateWithSlashesNoShift(
  date: Date | string | null | undefined
): string {
  if (!date) return '';
  try {
    const d = new Date(date);
    const offset = d.getTimezoneOffset();
    const adjustedDate = new Date(d.getTime() + offset * 60 * 1000);
    const formattedDate = new Intl.DateTimeFormat('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZone: 'America/New_York',
      timeZoneName: 'short',
    })
      .format(adjustedDate)
      .replace(', ', ' ');

    return formatDateWithSlashes(formattedDate);
  } catch (e) {
    return '';
  }
}
